import { useCallback, useEffect, useMemo, useState } from 'react'
import { useDispatch } from 'react-redux'
import { Link } from 'react-router-dom'
import styles from './Footer.module.sass'
import { actions } from 'modules/home/HomeReducer'
import {
  iconCurricula,
  iconPodcast,
  iconLiveEvents,
  iconNewsFeed,
  iconTwitter,
  iconLinkedin
} from 'components/Icons'
import PropTypes from 'prop-types'
import config from 'site.config'

const {
  sections: { footer }
} = config

const Footer = ({ setMenuOpen }) => {
  const dispatch = useDispatch()

  const breakpoint = 1040
  const currentYear = new Date().getFullYear()

  const [width, setWidth] = useState(window.innerWidth)

  useEffect(() => {
    const handleWindowResize = () => setWidth(window.innerWidth)
    window.addEventListener('resize', handleWindowResize)
    return () => window.removeEventListener('resize', handleWindowResize)
  }, [])

  const clearSearch = useCallback(() => {
    dispatch(actions.setSearchTerm(''))
    dispatch(actions.setSearchPanel(false))
  }, [dispatch])

  const menu = useMemo(
    () => [
      {
        title: 'Curricula',
        icon: iconCurricula,
        url: '/curricula'
      },
      {
        title: 'Podcasts',
        icon: iconPodcast,
        url: '/podcasts'
      },
      {
        title: 'Live Events',
        icon: iconLiveEvents,
        url: '/live-events'
      },
      {
        title: 'News Feed',
        icon: iconNewsFeed,
        url: '/#news'
      }
    ],
    []
  )

  const iconMap = useMemo(
    () => ({
      linkedin: iconLinkedin,
      twitter: iconTwitter
    }),
    []
  )

  return (
    <div
      data-testid="footer"
      role="button"
      tabIndex={0}
      className={styles.bg}
      style={{ cursor: 'default' }}
      onClick={setMenuOpen}
      onKeyDown={e => {
        if (e.key === 'Enter' || e.key === ' ') return setMenuOpen
      }}
    >
      <div className={styles.box}>
        <p className={[styles.psocial]}>
          {footer.social.title}
          {footer.social.links.map(({ title, icon, url }) => (
            <a
              href={url}
              target="_blank"
              rel="noreferrer"
              key={title}
              aria-label={'Visit ' + title}
            >
              {iconMap[icon]({ className: styles.imgSocial })}
            </a>
          ))}
        </p>
        <div className={styles.footer}>
          {width < breakpoint
            ? footer.links?.slice(0, 1).map(({ title }) => (
                <p key={title} className={styles.pcopy}>
                  {title?.replace('{year}', currentYear)}
                </p>
              ))
            : null}
          <p className={styles.pcopy}>
            {footer.links
              ?.slice(width < breakpoint ? 1 : 0)
              .map(({ url, title }) => {
                if (!url)
                  return (
                    <span
                      className={styles.a}
                      key={title}
                      style={{ textDecoration: 'none' }}
                    >
                      {title?.replace('{year}', currentYear)}
                    </span>
                  )
                const isExternalLink =
                  url.startsWith('http') || url.startsWith('//')
                return isExternalLink ? (
                  <a
                    href={url}
                    key={url}
                    className={styles.a}
                    target="_blank"
                    rel="noreferrer"
                  >
                    {title}
                  </a>
                ) : (
                  <Link to={url} key={url} className={styles.a}>
                    {title}
                  </Link>
                )
              })
              .reduce((prev, curr) => [prev, ` | `, curr])}
          </p>
        </div>
      </div>
      <div className={styles.mbox}>
        {menu.map(({ title, icon, url }) => {
          const html = (
            <>
              {icon({ style: { height: 20 }, className: styles.micon })}
              <p className={styles.mtext}>{title}</p>
            </>
          )
          return (
            <Link
              to={url}
              className={styles.mbutton}
              key={title}
              onClick={clearSearch}
              onKeyDown={e => {
                if (e.key === 'Enter' || e.key === ' ') clearSearch()
              }}
            >
              {html}
            </Link>
          )
        })}
      </div>
    </div>
  )
}
Footer.propTypes = {
  setMenuOpen: PropTypes.func
}

export default Footer
